import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTicketsList, updateTicketStatus } from "@/store/slices/ticketSlice";
import Swal from "sweetalert2";
import Toaster from "@/components/Toaster";
import { useTranslation } from "react-i18next";
import {
    Card,
    Input,
    Button,
    IconButton,
    Menu,
    MenuList,
    MenuHandler,
    MenuItem,
} from "@material-tailwind/react";
import {
    MagnifyingGlassIcon,
    Bars3BottomLeftIcon,
    InformationCircleIcon,
    XCircleIcon,
    XMarkIcon,
    ArrowDownTrayIcon,
} from "@heroicons/react/24/solid";
import { Tooltip } from "flowbite-react";
import dayjs from "dayjs";
import ScrollToTop from "@/components/ScrollToTop";
import AssignTicketModal from "./assign-ticket-modal";
import ShowLoader from "@/components/Loader";

export default function TicketList() {
    const { t } = useTranslation("ticket");
    const dispatch = useDispatch();
    const observer = useRef();
    const { user } = useSelector((state) => state.user);

    const { ticketsData, list_loading } = useSelector((state) => state.ticket || {});
    const tickets = ticketsData?.tickets || [];
    const counts = ticketsData?.counts || {
        total: 0,
        pending: 0,
        in_process: 0,
        completed: 0,
    };
    const [activeTab, setActiveTab] = useState(() => {
        const currentUserRole = user?.roles?.[0] || "";
        return currentUserRole === "Sales Representative" ? "in_process" : "pending";
    });
    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const [sortOrder, setSortOrder] = useState("desc");
    const [previewImage, setPreviewImage] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isActionRefreshing, setIsActionRefreshing] = useState(false);

    const priorityColorMap = {
        high: "bg-red-500",
        medium: "bg-yellow-500",
        low: "bg-green-500",
    };

    const fetchTickets = useCallback(
        (pageNumber = 1) => {
            return dispatch(
                getTicketsList({
                    tab: activeTab,
                    search: searchQuery,
                    page: pageNumber,
                    per_page: 10,
                    sort_order: sortOrder,
                })
            );
        },
        [dispatch, activeTab, searchQuery, sortOrder]
    );

    useEffect(() => {
        setPage(1);
        fetchTickets(1);
    }, [activeTab, searchQuery, sortOrder]);

    const handleAssignmentSuccess = async () => {
        setIsActionRefreshing(true);
        setPage(1);
        try {
            await fetchTickets(1).unwrap();
        } catch (error) {
            console.error("Failed to sync listing update:", error);
        } finally {
            setIsActionRefreshing(false);
        }
    };

    const handelSearch = (event) => {
        const value = event.currentTarget.value;

        if (event.key === "Enter") {
            setPage(1);
            setSearchQuery(value);
        } else if (event.key === "Backspace" && value.length === 0) {
            setPage(1);
            setSearchQuery("");
        }
    };

    const handleSearchClick = () => {
        setPage(1);
        setSearchQuery(searchInput);
    };

    const handelRemoveSearch = () => {
        setSearchInput("");
        setSearchQuery("");
        setPage(1);
    };

    const lastTicketElementRef = useCallback(
        (node) => {
            if (list_loading) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver((entries) => {
                if (
                    entries[0].isIntersecting &&
                    ticketsData?.pagination?.current_page < ticketsData?.pagination?.last_pages
                ) {
                    const nextPage = page + 1;
                    setPage(nextPage);
                    fetchTickets(nextPage);
                }
            });

            if (node) observer.current.observe(node);
        },
        [list_loading, ticketsData, page, fetchTickets]
    );

    const formatTicketDateTime = (dateString) => {
        if (!dateString) return { date: "N/A", time: "N/A" };
        const cleanDate = dayjs(dateString.replace(" ", "T"));

        const day = cleanDate.date();
        const getOrdinal = (d) => {
            if (d > 3 && d < 21) return "th";
            switch (d % 10) {
                case 1: return "st";
                case 2: return "nd";
                case 3: return "rd";
                default: return "th";
            }
        };

        return {
            date: `${day}${getOrdinal(day)} ${cleanDate.format("MMMM, YYYY")}`,
            time: cleanDate.format("h:mm A"),
        };
    };

    const currentUserRole = user?.roles?.[0] || "";

    const tabConfig = [
        {
            id: "pending",
            label: t("pendingTickets"),
            count: counts.pending,
        },
        {
            id: "in_process",
            label: t("inProcess"),
            count: counts.in_process,
        },
        {
            id: "completed",
            label: t("completedTickets"),
            count: counts.completed,
        },
    ].filter(
        (tab) =>
            currentUserRole !== "Sales Representative" || tab.id !== "pending"
    );

    const handleOpenAssignModal = (ticket) => {
        setSelectedTicket(ticket);
        setIsModalOpen(true);
    };

    const handleCloseTicket = async (ticket) => {
        const ticketLabel = `${ticket.ticket_name}` || ticket.rfq?.rfq_reference;

        Swal.fire({
            title: t("areYouSure"),
            text: t("closeTicketConfirmText", { ticketLabel }),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t("yes"),
            cancelButtonText: t("no"),
            customClass: {
                confirmButton: "bg-green-600 text-white px-5 py-2.5 rounded-md text-sm font-semibold mx-2 hover:bg-green-700 focus:outline-none",
                cancelButton: "bg-red-600 text-white px-5 py-2.5 rounded-md text-sm font-semibold mx-2 hover:bg-red-700 focus:outline-none"
            },
            buttonsStyling: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                setIsActionRefreshing(true);
                setPage(1);
                try {
                    const response = await dispatch(updateTicketStatus({
                        ticket_id: ticket.id,
                        current_status: "closed"
                    })).unwrap();

                    Toaster.success(response?.message || t("ticketClosedSuccess"));
                    await fetchTickets(1).unwrap();
                } catch (error) {
                    Toaster.error(error?.message || t("ticketCloseFailed"));
                    console.error("Failed to update status:", error);
                } finally {
                    setIsActionRefreshing(false);
                }
            }
        });
    };

    const handleDownloadImage = async (imageUrl) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;

            const fileName =
                imageUrl.split("/").pop()?.split("?")[0] || "attachment";

            link.download = fileName;
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
            Toaster.error(t("downloadImageFailed"));
        }
    };

    return (
        <div className="p-6 min-h-screen">
            <ScrollToTop />

            {isActionRefreshing && <ShowLoader />}

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-semibold text-gray-900">{t("ticketsManagement")} ({counts.total})</h2>
                    <button
                        className="ml-4 p-1 hover:bg-gray-100 rounded"
                        onClick={() => {
                            setPage(1);
                            fetchTickets(1);
                        }}
                        disabled={list_loading}
                    >
                        <img
                            src="/img/icons/reload.png"
                            alt={t("reloadAlt")}
                            className={`h-4 w-4 ${list_loading ? "animate-spin" : ""}`}
                        />
                    </button>
                </div>

                <div className="w-full lg:max-w-md flex items-center gap-2">
                    <div className="w-full relative max-w-md">
                        <Input
                            label={t("searchLabel")}
                            icon={!searchInput && <MagnifyingGlassIcon className="h-5 w-5" />}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyUp={handelSearch}
                            className="pr-10"
                        />
                        {searchInput && (
                            <IconButton variant="text" size="sm" className="!absolute right-1 top-1" onClick={handelRemoveSearch}>
                                <XCircleIcon className="h-5 w-5 text-red-500" />
                            </IconButton>
                        )}
                    </div>
                    {searchInput && (
                        <Button
                            variant="outlined"
                            className="flex items-center border-gray-300 capitalize font-bold bg-black text-white whitespace-nowrap"
                            onClick={handleSearchClick}
                        >
                            {t("searchButton")}
                        </Button>
                    )}
                </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4 mt-6">
                <div className="flex items-center gap-3">
                    <Menu>
                        <MenuHandler>
                            <Button variant="text" className="flex items-center gap-2 bg-white border border-gray-300 text-black normal-case py-2 px-4 shadow-sm">
                                <Bars3BottomLeftIcon className="h-4 w-4" />
                            </Button>
                        </MenuHandler>
                        <MenuList>
                            <MenuItem onClick={() => setSortOrder("desc")}>{t("sortNewestTickets")}</MenuItem>
                            <MenuItem onClick={() => setSortOrder("asc")}>{t("sortOldestTickets")}</MenuItem>
                        </MenuList>
                    </Menu>
                </div>
            </div>

            <div className="border-b border-gray-200 mb-6 flex gap-8 mt-6">
                {tabConfig.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => {
                            setActiveTab(tab.id);
                            setPage(1);
                        }}
                        className={`pb-3 text-sm font-bold tracking-wide relative transition-all ${activeTab === tab.id ? "text-gray-900" : "text-gray-400 hover:text-gray-600"
                            }`}
                    >
                        {tab.label} ({tab.count ?? 0})
                        {activeTab === tab.id && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-900 rounded-full" />}
                    </button>
                ))}
            </div>

            <div className="mt-6 space-y-4">
                {tickets.map((ticket, index) => {
                    const { date, time } = formatTicketDateTime(ticket.created_at);
                    const isLastElement = tickets.length === index + 1;

                    return (
                        <Card
                            key={ticket.id}
                            ref={isLastElement ? lastTicketElementRef : null}
                            shadow={false}
                            className="border border-gray-200 rounded-lg p-5 bg-white shadow-sm"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">

                                <div className="md:col-span-2">
                                    <div className="flex items-center gap-2">
                                        {activeTab !== "pending" && ticket.assigned_user?.priority && (
                                            <span
                                                className={`w-2.5 h-2.5 rounded-full ${priorityColorMap[ticket.assigned_user.priority.toLowerCase()] || "bg-gray-400"
                                                    }`}
                                                title={`${t("priorityTooltip")}: ${ticket.assigned_user.priority}`}
                                            ></span>
                                        )}
                                        <h4 className="font-bold text-gray-900 text-sm">
                                            {ticket.rfq?.rfq_reference || `TKT-${ticket.ticket_name}`}
                                        </h4>
                                        <Tooltip
                                            placement="top"
                                            className="bg-white"
                                            content={
                                                <div className="p-1 text-xs text-black">
                                                    <p className="font-semibold mb-1">{t("ticketDetails")}</p>
                                                    <p>{t("ticketId")}: {ticket.id}</p>
                                                    <p>{t("ticketRef")}: {ticket.ticket_name}</p>
                                                    <p>
                                                        {t("status")}: {ticket.current_status ?
                                                            ticket.current_status.charAt(0).toUpperCase() + ticket.current_status.slice(1) : t("na")}
                                                    </p>
                                                </div>
                                            }
                                        >
                                            {/* <InformationCircleIcon className="h-4 w-4 text-blue-gray-400 cursor-pointer hover:text-black" /> */}
                                            <img
                                                src="/img/icons/exclamation-circle.png"
                                                alt="Info"
                                                className="ml-4 h-4 w-4 cursor-pointer hover:opacity-80"
                                            />
                                        </Tooltip>
                                    </div>
                                    <div className="max-w-xs text-xs text-gray-500 mt-2">
                                        <div className="mb-2 font-semibold text-black">
                                            {t("ticketRef")}: {ticket.ticket_name}
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t("orderId")}: {ticket.rfq?.order_name || t("na")}
                                    </p>
                                </div>

                                <div className="md:col-span-4 space-y-2">
                                    <p className="text-sm font-semibold text-gray-900">
                                        {t("issue")}: {ticket.ticket_subject?.subject || t("generalIssue")}
                                    </p>
                                    <p className="text-xs text-gray-600 leading-relaxed max-w-prose">
                                        <span className="font-xl text-gray-500 block mb-0.5">{t("issueDetails")}</span>
                                        {ticket.ticket_reason}
                                    </p>

                                    {ticket.images && ticket.images.length > 0 && (
                                        <div className="flex gap-2 pt-1 flex-wrap">
                                            {ticket.images.map((img) => (
                                                <div
                                                    key={img.id}
                                                    onClick={() => setPreviewImage(img.image)}
                                                    className="w-14 h-14 rounded overflow-hidden bg-gray-50 shadow-sm cursor-pointer hover:opacity-80 transition-all"
                                                >
                                                    <img
                                                        src={img.image}
                                                        alt={t("attachmentPreviewAlt")}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => { e.target.src = "https://placehold.co/48x48?text=Img"; }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className={`${activeTab === "completed" ? "md:col-span-2" : "md:col-span-3"} text-xs md:text-sm text-gray-900 space-y-1 font-xl`}>
                                    <p className="font-semibold text-gray-900 truncate" title={ticket.email}>
                                        {ticket.email}
                                    </p>
                                    <p className="text-gray-500">
                                        {ticket.mobile_number || ticket.assigned_user?.mobile_number || t("noContactProvided")}
                                    </p>

                                    {activeTab === "in_process" && currentUserRole !== "Sales Representative" && (
                                        <>
                                            <p className="text-gray-900 pt-1">
                                                <span className="text-gray-600 font-medium">{t("salesRep")}:</span>{" "}
                                                {ticket.assigned_user?.full_name || t("unassigned")}
                                            </p>
                                            {ticket.assigned_user?.mobile_number && (
                                                <p className="text-gray-500 text-xs truncate">
                                                    {ticket.assigned_user.mobile_number}
                                                </p>
                                            )}
                                            {ticket.assigned_user?.notes && (
                                                <p className="text-gray-900 pt-1">
                                                    <span className="text-gray-600 font-medium">{t("note")}:</span>{" "}
                                                    {ticket.assigned_user.notes}
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>

                                <div className="md:col-span-2 text-xs md:text-sm text-gray-900 space-y-1 font-xl">
                                    <p className="font-semibold text-gray-900"><span>{t("date")}:</span> {date}</p>
                                    <p className="font-medium text-gray-500"><span >{t("time")}:</span> {time}</p>
                                </div>

                                {activeTab === "completed" ? (
                                    <>
                                        <div className="md:col-span-1 text-xs md:text-sm space-y-1">
                                            <span className="text-gray-900 font-medium block">{t("statusLabel")}</span>
                                            <span className="text-green-600 font-semibold capitalize px-0.5 py-0.5 rounded-md inline-block">
                                                {ticket.current_status === "open"
                                                    ? t("statusOpen")
                                                    : ticket.current_status === "closed"
                                                        ? t("statusClosed")
                                                        : t("na")}
                                            </span>
                                        </div>

                                        {/* Sales Rep Column (Span 1) */}
                                        {currentUserRole !== "Sales Representative" && (
                                            <div className="md:col-span-1 text-xs md:text-sm space-y-1">
                                                <span className="text-gray-900 font-medium block">{t("salesRepLabel")}</span>
                                                <p className="font-semibold text-gray-900 break-words">
                                                    {ticket.assigned_user?.full_name || t("unassigned")}
                                                </p>
                                                {ticket.assigned_user?.mobile_number && (
                                                    <p className="text-gray-500 text-xs break-words">
                                                        {ticket.assigned_user.mobile_number}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="md:col-span-1 flex md:flex-col md:justify-center items-center gap-2">
                                        {(() => {
                                            const isAuthorizedToUpdate =
                                                currentUserRole === "Administrator" ||
                                                currentUserRole === "Sub Admin" ||
                                                currentUserRole === "Sales Representative";

                                            if (!isAuthorizedToUpdate) return null;

                                            return (
                                                <>
                                                    {activeTab === "pending" && (
                                                        <Button
                                                            size="sm"
                                                            className="bg-[#F0B100] hover:bg-[#D69E00] text-gray-900 shadow-none hover:shadow-none font-semibold px-4 py-2 rounded-md w-full md:w-auto normal-case flex items-center justify-center gap-1.5 transition-colors"
                                                            onClick={() => handleOpenAssignModal(ticket)}
                                                        >
                                                            {t("assignButton")}
                                                        </Button>
                                                    )}

                                                    {activeTab === "in_process" && (
                                                        <Menu placement="bottom-end">
                                                            <MenuHandler>
                                                                <Button
                                                                    size="sm"
                                                                    className="bg-[#F0B100] hover:bg-[#D69E00] text-gray-900 shadow-none hover:shadow-none font-semibold px-4 py-2 rounded-md w-full md:w-auto normal-case flex items-center justify-center gap-1.5 transition-colors"
                                                                >
                                                                    {t("statusButton")}
                                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-900 stroke-[0.5]">
                                                                        <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                                                                    </svg>
                                                                </Button>
                                                            </MenuHandler>
                                                            <MenuList className="z-[999] min-w-[130px] p-1 bg-white border border-gray-100 shadow-lg rounded-md">
                                                                <MenuItem
                                                                    onClick={() => handleCloseTicket(ticket)}
                                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50/50 font-semibold text-xs py-2 px-3 rounded transition-colors"
                                                                >
                                                                    {t("closeTicket")}
                                                                </MenuItem>
                                                            </MenuList>
                                                        </Menu>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}

                            </div>
                        </Card>
                    );
                })}

                {list_loading && (
                    <div className="flex justify-center py-6">
                        <div role="status">
                            <svg className="inline w-8 h-8 text-gray-200 animate-spin fill-green-500" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                            </svg>
                        </div>
                    </div>
                )}

                {!list_loading && tickets.length === 0 && (
                    <div className="py-12 text-center text-sm font-medium text-gray-500 bg-white border border-gray-200 rounded-lg shadow-sm">
                        {t("noTicketsFound")}
                    </div>
                )}
            </div>

            {previewImage && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
                    onClick={() => setPreviewImage(null)}
                >
                    <div
                        className="relative max-w-3xl max-h-[85vh] p-2 bg-white rounded-xl shadow-2xl overflow-hidden mx-4 flex flex-col items-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="absolute top-3 right-3 flex gap-2 z-50">
                            <button
                                type="button"
                                onClick={() => handleDownloadImage(previewImage)}
                                className="p-2 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors"
                                title={t("downloadImage")}
                            >
                                <ArrowDownTrayIcon className="h-5 w-5" />
                            </button>

                            <button
                                type="button"
                                onClick={() => setPreviewImage(null)}
                                className="p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                                title={t("close")}
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>
                        <img src={previewImage} alt={t("attachmentFullPreview")} className="w-full h-auto max-h-[80vh] object-contain rounded-lg" />
                    </div>
                </div>
            )}

            <AssignTicketModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setSelectedTicket(null);
                }}
                ticketData={selectedTicket}
                onSuccess={handleAssignmentSuccess}
            />
        </div>
    );
}
